/**
 * The WORDS for taking a bank holiday off a calendar, putting it back, and adding your
 * own — in one place, so both screens that offer those actions say the same thing.
 *
 * ⚠️ THE WORDING IS THE PART THAT DRIFTS. The endpoints are shared already; what goes
 * wrong is one screen saying "deleted" and another saying "removed from your calendar"
 * for the identical action, and an admin having to work out whether they mean the same
 * thing. Signup and the Holidays page both offer these from 13/08/2026, so this stopped
 * being a hypothetical.
 *
 * Pure. Checked by `node src/lib/holidayActions.checks.mjs`.
 */

/**
 * ⚠️ TWO DIFFERENT ACTIONS BEHIND ONE BIN ICON, and the question has to match which.
 *
 * A company's OWN day is really deleted. A bank holiday is a PLATFORM row shared by every
 * customer, so it cannot be deleted — the server records an opt-out instead. Telling an
 * admin "deleted" when nothing was deleted is a lie they would eventually catch.
 */
export const removeQuestion = (holiday) => (holiday?.is_custom
  ? `Delete "${holiday.name}"? It's your own company day, so it will be gone for good.`
  : `Take "${holiday?.name}" off your calendar? Your team will be expected to work it. `
    + 'You can put it back at any time.');

/**
 * What to say afterwards. The server's own message wins when it sends one.
 *
 * ⚠️ SAY WHAT HAPPENS TO THEM. An earlier version reassured the admin that "other
 * companies are unaffected" — true, and platform plumbing leaking into customer copy. A
 * customer neither knows nor cares that the row is shared.
 */
export const removedMessage = (holiday, serverMessage) => serverMessage
  || (holiday?.is_custom ? 'Holiday deleted' : 'Removed from your calendar');

export const restoredMessage = (name) => `${name} is back on your calendar`;

/** The hover text on the bin, which has the same two meanings as the question. */
export const removeTitle = (holiday) => (holiday?.is_custom
  ? 'Delete this company day'
  : "Remove from your company's calendar");

/**
 * Is this add-a-day form ready to submit? Returns an error string, or null when it's fine.
 *
 * ⚠️ A BLANK NAME OR DATE IS A 422 FROM THE API, which surfaces as a red toast full of
 * validation jargon. Catching it here means the admin is told the useful thing instead.
 */
export const addDayProblem = ({ name, date } = {}) => {
  if (!String(name || '').trim()) return 'Give the day a name';
  if (!String(date || '').trim()) return 'Choose a date';
  return null;
};
